import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { lensPath, set } from 'ramda';

import GoalsForm from './Form';
import Title from '../shared/Title';
import { fillSpace, breakpoints } from '../../constants/styles';
import {
  stringToNumber,
  getGoalNames,
  iOSSafari,
  getMaxQuestions,
} from '../../constants/utils';

import { HEADER_HEIGHT, FlowHeader } from './styles';

const SCROLL_OFFSET = 350;

const StyledDebtConsolidationFlow = styled.div`
  display: flex;
  flex-direction row;
  ${fillSpace}
`;

const FlowContent = styled.div`
  display: flex;
  align-items: center;
  height: calc(100vh - 50px);
  margin-top: ${HEADER_HEIGHT}px;
  padding: 0 20px;
  background-color: white;

  ${breakpoints.tablet} {
    width: 70%;
    margin-left: 15%;
    margin-right: 15%;
  }

  ${breakpoints.tabletLandscape} {
    width: 60%;
    margin-left: 20%;
    margin-right: 20%;
  }
`;

class DebtConsolidationFlow extends Component {
  state = {
    isFinished: false,
    questionnaire: [],
    focusedGoal: 0,
    focusedQuestion: 0,
    questionsAnswered: [],
    totalQuestions: 0,
    globalDitchedQuestions: 0,
    inputList: [],
    howManyDitchedMemory: [],
    debtForm: [],
    userAge: null,
  };

  componentDidMount = () => {
    this.initializeForm(this.props);
    this.setQuestionAsFocused();
    document.addEventListener('keyup', this.keyPress, false);
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.keyPress, false);
  }

  setGoalAsFocused = (goalIndex = 0) => {
    this.setState(state => ({
      ...set(lensPath(['questionnaire', goalIndex, 'isFocused']), true, state),
      focusedGoal: goalIndex,
    }));
  }

  setGoalAsAnswered = (goalIndex = 0) => {
    this.setState(state => ({
      ...set(lensPath(['questionnaire', goalIndex, 'isAnswered']), true, state),
    }));
  }

  setQuestionAsFocused = (goalIndex = 0, questionIndex = 0) => {
    this.setGoalAsFocused(goalIndex);
    this.setState(state => ({
      ...set(lensPath(['questionnaire', goalIndex, 'questions', questionIndex, 'isFocused']), true, state),
      focusedGoal: goalIndex,
      focusedQuestion: questionIndex,
    }));
  }

  setAllAsBlur = (cb) => {
    const { questionnaire } = this.state;
    console.log(questionnaire);
    this.setState({
      ...this.state,
      questionnaire: questionnaire.map(goal => ({
        ...goal,
        isFocused: false,
        questions: goal.questions.map(question => ({
          ...question,
          isFocused: false,
        })),
      })),
    }, cb);
  }

  setGoalAnswers = (values, goalName, goalIndex, cb) => {
    this.setState(state => ({
      ...set(lensPath(['debtForm', goalIndex]),
        this.formatValues2({ ...values }), state),
    }), () => { cb(); });
  }

  formatValues2 = (values) => {
    // eslint-disable-next-line camelcase, object-curly-newline
    const { debt_1 = '', debt_2 = '', type_of_card_1 = '', type_of_card_2 = '', bank_1 = '', bank_2 = '', payment_periods = 0, ...rest } = values;

    return {
      debt_1: stringToNumber(debt_1),
      debt_2: stringToNumber(debt_2),
      type_of_card_1: type_of_card_1.substring(0, type_of_card_1.length - 1),
      type_of_card_2: type_of_card_2.substring(0, type_of_card_2.length - 1),
      bank_1: bank_1.substring(0, bank_1.length - 1),
      bank_2: bank_2.substring(0, bank_2.length - 1),
      payment_periods,
      ...rest,
    };
  }

  formatValues = (values) => {
    const {
      age,
      expectedAge,
      moneyRequired,
      initialSavings,
      ...rest
    } = values;
    const yearstoExpense = expectedAge - age;

    return {
      ...rest,
      age,
      current_savings: stringToNumber(initialSavings),
      money_required: stringToNumber(moneyRequired),
      years_to_expense: yearstoExpense || 0,
    };
  }

  initializeForm = ({ questionIndex = 0, questionnaire, ditchedQuestions = 0 }, cb) => {
    const { globalDitchedQuestions, howManyDitchedMemory } = this.state;
    console.log(questionnaire);
    this.setState({
      questionnaire,
      totalQuestions: getMaxQuestions(questionnaire) - globalDitchedQuestions - ditchedQuestions,
      globalDitchedQuestions: globalDitchedQuestions + ditchedQuestions,
      howManyDitchedMemory: [...howManyDitchedMemory, [questionIndex, ditchedQuestions]],
      goalNames: getGoalNames(questionnaire),
    }, () => {
      this.updateInputList(cb);
    });
  }

  updateInputList = (cb) => {
    const inputsFound = document.querySelectorAll('.question');
    const inputList = [];
    inputsFound.forEach((input) => {
      inputList.push(input.id);
    });
    this.setState({ inputList }, cb);
  }

  injectNewQuestions = (newQuestions = [], goalIndex, questionIndex, ditchedQuestions, cb) => {
    const { questionnaire } = this.state;
    const newQuestionnaire = [...questionnaire];
    newQuestionnaire[goalIndex].questions
      .splice(questionIndex + 1, 0, ...[...newQuestions]);
    newQuestionnaire[goalIndex].questions[questionIndex].lastSaved = ditchedQuestions;
    this.initializeForm({ questionIndex, questionnaire: newQuestionnaire, ditchedQuestions }, cb);
  }

  removeNewQuestions = (goalIndex, questionIndex, ditchedQuestions, cb) => {
    const { questionnaire, questionsAnswered } = this.state;
    const newQuestionnaire = [...questionnaire];

    /*
    // TODO: If you uncomment this line, add howManyDitchedMemory to the destructuring
    let updateGlobalDitched = 0;
    let howManySteps = 0;
    for (let i = 0; i < howManyDitchedMemory.length; i += 1) {
      if (howManyDitchedMemory[i][0] < questionIndex) {
        updateGlobalDitched += howManyDitchedMemory[i][1];
      } else {
        howManySteps = i;
        break;
      }
    }

    // console.log(updateGlobalDitched);
    // console.log(howManySteps);

    // Remove all the extra memory and set the globalDitched again
    this.setState({
      howManyDitchedMemory: howManyDitchedMemory.splice(howManySteps),
      globalDitchedQuestions: updateGlobalDitched,
    });

    // console.log(questionIndex);
    // console.log("past",globalDitchedQuestions);
    // console.log("future",updateGlobalDitched);
    // console.log(newQuestionnaire);
    // console.log(newQuestionnaire[goalIndex]);

    */

    if (newQuestionnaire[goalIndex]) {
      for (let i = questionIndex + 1; i < newQuestionnaire[goalIndex].questions.length;) {
        if (!newQuestionnaire[goalIndex].questions[i].base) {
          newQuestionnaire[goalIndex].questions.splice(i, 1);
        } else {
          break;
        }
      }
    }

    // Esta es una súper mexicanada: Lo que necesitaba resolver era lo siguiente... Cada vez que quitabamos
    // preguntas del formulario, la cantidad total de preguntas se actualizaba correctamente, sin embargo
    // la cantidad de preguntas ya contestadas se mantenía, lo que hacía que potencialmente el usuario acabara sin
    // realmente haber acabado. Entonces intenté de muchísimas maneras cambiar el valor de preguntas ya contestadas,
    // pero por alguna extraña razón el valor seguía regresando a la cantidad de respuestas que existen en el JSON.
    // La solución fue que en vez de disminuir la cantidad de preguntas contestadas, lo que hice fue incrementar la
    // cantidad de preguntas restantes. Esto no reflejará la realidad pero proveera una buena experiencia de usuario.
    if (questionsAnswered[0]) {
      this.initializeForm({
        questionnaire: newQuestionnaire,
        ditchedQuestions: -newQuestionnaire[goalIndex].questions[questionIndex].lastSaved,
      }, cb);
    } else {
      this.initializeForm({
        questionnaire: newQuestionnaire,
        ditchedQuestions,
      }, cb);
    }
  }

  handleQuestionFocus = (ev, index, goalId) => {
    const { questionnaire } = this.state;
    const element = ev.target;
    const currentGoalIndex = questionnaire.findIndex(goal => goal.value === goalId);
    this.setAllAsBlur(() => {
      this.setQuestionAsFocused(currentGoalIndex, index);
      /** We need to do it with this method (getBoundingClientRect) so we can preserve the
        * right offset even with relative positioned elements.
        */
      const offsetTop = element.getBoundingClientRect().top + window.scrollY;
      if (!iOSSafari) {
        window.scrollTo({ top: offsetTop - SCROLL_OFFSET });
      }
    });
  }

  handleQuestionChange = (ev) => {
    this.setState({ isFinished: false });
    const inputId = ev.target.id;
    const inputName = ev.target.name;
    const inputValue = ev.target.value === '' ? null : Number(ev.target.value);

    if (inputName === 'age') {
      this.setState({ userAge: inputValue });
    }
    this.setState({ currentInputId: inputId });
  }

  handleQuestionBlur = (ev, {
    values = [], goalAnswered, goalIndex, goalName,
  }) => {
    const arrValues = Object.keys(values).map(key => values[key]);
    this.setState(state => ({
      ...set(lensPath(['questionsAnswered', goalIndex]), arrValues.length, state),
    }));
    this.setGoalAnswers(values, goalName, goalIndex, () => {
      if (goalAnswered) {
        this.setGoalAsAnswered(goalIndex);
      }
    });
  }

  handleFormAnswered = (data, cb) => {
    const {
      values = [], goalAnswered, goalIndex, goalName,
    } = data;
    const arrValues = Object.keys(values).map(key => values[key]);
    this.setState(state => ({
      ...set(lensPath(['questionsAnswered', goalIndex]), arrValues.length, state),
    }));
    this.setGoalAnswers(values, goalName, goalIndex, () => {
      if (goalAnswered) {
        this.setGoalAsAnswered(goalIndex);
      }
      cb();
    });
  }

  handleStepSubmit = (questionId = '') => {
    const { debtForm } = this.state;
    const { onStepSubmit } = this.props;
    const tempObject = debtForm;
    // This is used to prevent missing last number of user input if it's the last question of flow
    if (!document.getElementById(questionId).readOnly) {
      const { goalNames } = this.state;
      tempObject[debtForm.length - 1][questionId.replace(`${goalNames[debtForm.length - 1]}-`, '')] = document.getElementById(questionId).value;
    }
    onStepSubmit(debtForm);
  }

  goToNextInput = (questionId) => {
    const { inputList, questionnaire, focusedGoal } = this.state;
    const currentInputIndex = inputList.findIndex(id => id === questionId);
    const nextInputName = inputList[currentInputIndex + 1];

    const nextInput = document.querySelector(`#${nextInputName}`);
    const submitContainer = document.querySelector(`#goal-submit-${questionnaire.length}`);
    const submitButton = document.querySelector(`#btn-submit-${focusedGoal}`);
    if (nextInput) {
      nextInput.focus();
      if (!iOSSafari) {
        window.scrollTo({ top: nextInput.offsetTop - SCROLL_OFFSET });
      }
    } else {
      this.setState({ isFinished: true });
      this.handleStepSubmit(questionId);
    }
  }

  keyPress = (ev) => {
    const { currentInputId } = this.state;
    if (ev.keyCode === 13 || ev.keyCode === 9) {
      ev.preventDefault();
      this.goToNextInput(currentInputId);
    }
  }

  render() {
    const {
      questionnaire,
      isFinished,
      userAge,
    } = this.state;
    console.log(questionnaire);
    return (
      <StyledDebtConsolidationFlow>
        <FlowHeader>
          {/*
          <ProgressBar current={totalAnswers + 0.5} total={this.state.totalQuestions - 0.5}>
            {totalAnswers} - {this.state.totalQuestions}
          </ProgressBar>
          */}
          <Title level={5} color="white" styles={css`margin-bottom: 0px;`} medium>Calculando tu ahorro</Title>
        </FlowHeader>
        <FlowContent>
          <GoalsForm
            questionnaire={questionnaire}
            onQuestionFocus={this.handleQuestionFocus}
            onQuestionChange={this.handleQuestionChange}
            onQuestionBlur={this.handleQuestionBlur}
            onStepSubmit={this.handleStepSubmit}
            goToNextInput={this.goToNextInput}
            isFinished={isFinished}
            userAge={userAge}
            getSubmitRef={(ref) => { this.submitContainer = ref; }}
            injectNewQuestions={this.injectNewQuestions}
            removeNewQuestions={this.removeNewQuestions}
            handleFormAnswered={this.handleFormAnswered}
          />
        </FlowContent>
      </StyledDebtConsolidationFlow>
    );
  }
}

DebtConsolidationFlow.propTypes = {
  onStepSubmit: PropTypes.func,
};

DebtConsolidationFlow.defaultProps = {
  onStepSubmit: () => { },
};

export default DebtConsolidationFlow;
